import { Typography } from "@/components/shared/typography";
import {
  Button,
  View,
  Image,
  Dialog,
  DialogContent,
  CardFooter,
  CardContent,
  Card,
  Badge,
} from "@/components/ui";
import ratingChecklistIcon from "@/assets/images/3d-icons/rating-checklist.png";
import { useClientReferralCodeQuery } from "@/gqlhooks/client/useClientReferralCodeQuery";
import { IconButton } from "@/components/ui/icon-button";
import { copyClipboard } from "@/lib/helpers/copy-clipboard";
import { Divider } from "@/components/shared/divider";
import { Info } from "@/components/shared/icons";
import { useSharerModalStore } from "@/store/sharer";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";
import { useAuthState } from "@/store/auth";
import { sendAnalyticData } from "@/lib/monitoring/analytics";

type RateVisitSuccessProps = {
  open: boolean;
  rating: number;
  onClose: () => void;
};

export function RateVisitSuccess({
  open,
  onClose,
  rating,
}: RateVisitSuccessProps) {
  const showReferralEligible = rating >= 4;
  const { code, loading } = useClientReferralCodeQuery();
  const { setOpen } = useSharerModalStore();

  const clientId = useAuthState((state) => state.data.userInfo.id);
  const clientName = useAuthState(
    (state) => `${state.data.userInfo.id} ${state.data.userInfo.id}`,
  );
  const isVariantFlag =
    showReferralEligible &&
    PosthogAnalytics.getFeatureFlag("rate_visit_success") === "variant";

  const trackSharerReferralClick = () => {
    sendAnalyticData({
      name: "sharer_referral_click",
      values: {
        clientId,
        clientName,
      },
    });
  };

  const openShareModal = () => {
    setOpen(true);
    trackSharerReferralClick();
  };

  const copyReferralCode = () => {
    copyClipboard(code ?? "");
    trackSharerReferralClick();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[460px] p-4">
        <View className="flex flex-col items-center justify-center gap-6 bg-white text-center">
          <View className="flex flex-col items-center gap-3">
            <Image
              src={ratingChecklistIcon}
              alt="Rating checklist"
              width={56}
              height={56}
            />
            <View className="flex flex-col items-center gap-2">
              <Typography variant="label-md" className="text-foreground">
                Thanks for rating your booking! 🌟
              </Typography>
              <Typography
                variant="body-sm"
                className="text-foreground-secondary"
              >
                Your feedback helps us serve you better
              </Typography>
            </View>
          </View>

          {isVariantFlag && (
            <View className="mb-6 flex flex-col gap-2">
              <View className="flex flex-col items-center gap-2 border-t border-border pt-4 text-center">
                <Typography variant="label-sm">Give $15, Get $15</Typography>
                <Typography
                  variant="body-sm"
                  className="text-foreground-intermediate"
                  align="center"
                >
                  Every friend you refer gets $15 off their first booking, and
                  you get $15 credit when they book.
                </Typography>
              </View>

              <Card className="w-full">
                <CardContent className="p-3">
                  <View className="flex flex-row items-center justify-between gap-0">
                    <Badge variant="primary" shape="chip" outline={false}>
                      <View className="flex flex-row gap-2">
                        <Typography variant="label-md" color="brand-primary">
                          {loading ? "..." : code}
                        </Typography>
                        <IconButton
                          variant="ghost"
                          iconName="copy"
                          className="p-0"
                          size={"md"}
                          color="CTA2"
                          disabled={loading}
                          onClick={copyReferralCode}
                        />
                      </View>
                    </Badge>
                    <Button
                      variant="secondary"
                      color="CTA2"
                      size={"md"}
                      rounded="full"
                      disabled={loading}
                      onClick={openShareModal}
                      iconAlignment={"end"}
                      iconName="shareFat"
                      iconColor="CTA2"
                      iconSize={"md"}
                    >
                      Share
                    </Button>
                  </View>
                </CardContent>
                <Divider />
                <CardFooter className="flex flex-row items-center gap-1 px-3 pb-3 pt-2">
                  <Info />
                  <Typography variant={"caption-md"}>
                    Earn $15 when your friend completes their first booking
                  </Typography>
                </CardFooter>
              </Card>
            </View>
          )}

          <View className="w-full">
            <Button
              variant="primary"
              color="CTA"
              onClick={onClose}
              size={"lg"}
              className="w-full"
            >
              OK!
            </Button>
          </View>
        </View>
      </DialogContent>
    </Dialog>
  );
}
